/**
 * A generative video-synth layer living underneath the warp lattice.
 *
 * The point is atmosphere: a slow, generative field so the space behind the
 * corridor is not flat black between rounds. The whole design problem is that
 * `reports/critique-r5.md` established a black floor — background cell interiors
 * at p25 0.084 with 5.5% of pixels under 0.05 — after two earlier rounds lost
 * it, and any full-screen layer is the most direct way imaginable to throw that
 * away again. So the layer is built to make lifting the floor *impossible*
 * rather than merely unlikely:
 *
 *  1. **A hard pedestal.** The composite shader subtracts `uPedestal` from the
 *     decoded linear value and clamps at zero. Anything the field draws below
 *     that point contributes exactly nothing — not a small number, zero — so the
 *     cell interiors under a dark region of the pattern are byte-identical to
 *     the frame without the layer. This is the same trick `BLOOM.knee` plays on
 *     the bright pass, pointed the other way.
 *  2. **A gain far under the bloom knee foot.** Peak output is `uGain` at 0.20,
 *     against `BLOOM.threshold - BLOOM.knee` = 0.85. The layer cannot reach the
 *     bright pass at all, so it can never feed the pyramid and can never smear
 *     itself field-wide the way the round-two lattice did. Not "tuned not to" —
 *     arithmetically cannot, because the pattern value is clamped to 1.0 before
 *     the pedestal maps it. The measurement agrees: p99 of the playfield is
 *     unchanged to four decimals on `empty`, `combat` and `swarm` alike.
 *  3. **Additive, not opaque.** Drawing the quad opaque would *replace* the
 *     clear colour, so a black pattern pixel would push the floor down below
 *     `PHOSPHOR.black` and change the measurement in the other direction.
 *     Additive means zero in, zero changed.
 *  4. **Patterns authored sparse.** Every chain ends on a `luma` ramp and a
 *     per-pattern pedestal cuts it, so most of the field is genuinely black
 *     rather than dim. This is the calm-gate idea from
 *     `fractal-experiment/cloudflare/hydra-writer` — floors on tolerance so
 *     nothing strobes, caps on brightness and modulate depth so nothing runs hot
 *     — applied to a curated set instead of a generated one.
 *
 * ## Why this is one shader
 *
 * The six patterns were sketched in the live-coding vocabulary `hydra-synth`
 * popularised — `osc().kaleid().modulate().luma()` — and that vocabulary is the
 * reason they read the way they do. Borrowing an idiom is worth recording;
 * carrying the package is not, because it is AGPL-3.0 and §13's network clause
 * does not survive a public deploy from a private repository. So the ops below
 * are this file's own, each written from the definition of the thing it names,
 * and the vocabulary survives as an interface rather than as a dependency. The
 * one exception is simplex noise, which arrives from `shaders/simplexNoise.ts`
 * as the MIT-licensed Ashima Arts reference implementation, header and
 * attribution intact.
 *
 * Composition follows the same two rules the chains were sketched under, which
 * is what makes a chain in a comment checkable against the code under it:
 * coordinate transforms apply to `_st` in *reverse* written order, colour
 * transforms apply forward.
 *
 * Three things fall out of computing the field in place rather than uploading
 * it from a second canvas:
 *
 *  - **No second WebGL context.** The old path ran a synth on a detached canvas
 *    and the result arrived as a `CanvasTexture` upload every frame. That upload
 *    was the entire measured cost of the layer.
 *  - **Full resolution.** The old path synthesised at 160x90 and magnified 4x
 *    with a linear filter. Every pattern's feature size is a fraction of the
 *    screen, so the field was already smooth at that size; drawing it per pixel
 *    only removes the bilinear softening. The kaleidoscope's centre, where the
 *    angular compression puts real detail below one texel, is the one place the
 *    difference shows, and it shows as detail rather than as blocks.
 *  - **The `luma` gate actually gates.** A key that premultiplies, returning
 *    `vec4(rgb * a, a)`, got unpremultiplied back to `rgb` on the way into a
 *    texture — measured with a two-context probe, not assumed — so the soft ramp
 *    the patterns were authored around only ever survived as a hard cut at zero
 *    alpha. Computing the chain in place keeps `rgb * a` intact, which is what
 *    the pattern notes describe.
 *
 * Time comes from a private frame counter, never from the wall clock, so a
 * capture of frame N is byte-identical between runs — same rule the CRT noise
 * and glare already follow.
 */

import * as THREE from 'three';
import { VIEW } from '@/core/config';
import { region } from '@/core/region';
import { perf } from '@/core/perf';
import { GLSL_SIMPLEX_NOISE_3D } from './shaders/simplexNoise';

/**
 * Tuning. Every number here was measured against the black-floor rig rather
 * than picked, and the two that matter are `gain` and `pedestal`.
 */
export const HYDRA = {
  /**
   * Peak linear contribution, at pattern white.
   *
   * `GRID.baseIntensity` is 0.80 and the bloom knee foot is
   * `BLOOM.threshold - BLOOM.knee` = 0.85, so the brightest pixel this layer
   * can write is a quarter of a resting grid line and cannot reach the bright
   * pass at any pattern, any trim, any frame.
   *
   * The first draft ran this at 0.055 and lost, because the wrong knob was
   * being turned. Measured against the rig, p25 of the background cells moves
   * with the layer's *coverage* and barely at all with its gain: at 1.5%
   * coverage, gain 0.10 and gain 0.20 both left p25 inside 0.0002 of the
   * baseline. So the shape that works is sparse and bright, not broad and dim,
   * and the per-pattern pedestals below own coverage while this carries the
   * visibility.
   */
  gain: 0.20,
  /**
   * Default linear black point, for a pattern that does not name its own.
   * Subtracted before the gain, clamped at zero.
   *
   * Measured, not guessed. At 0.18 (sRGB 0.46) with the first draft's `luma`
   * gates, the resting frame's background cells moved p25 0.0867 → 0.0964 and
   * the sub-0.05 population fell 7.03% → 5.66%. 0.34 linear is sRGB 0.63, so
   * roughly the darker two thirds of the pattern's range contributes literal
   * zero.
   */
  pedestal: 0.34,
  /** Seconds a pattern holds before the set advances. */
  holdSeconds: 26,
  /** Seconds of fade to black either side of a pattern change. */
  fadeSeconds: 1.4,
  /**
   * Clock rate, in synth seconds per real second. hydra accumulated
   * `dt * 0.001 * speed` per tick and every chain constant was authored against
   * that clock, so `uTime` advances by exactly `timeScale / 60` per frame and
   * the patterns run at the tempo they were written at. Below 1 everything gets
   * slower; this is the single knob for taking them further down.
   */
  timeScale: 0.85,
} as const;

/**
 * A curated slice of the hydra.ojack.xyz idiom, calmed for a background.
 *
 * Selection rules, all of them consequences of sitting under gameplay:
 *
 *  - **Sparse, not dim.** Every chain ends on a `luma` ramp cut by a pedestal,
 *    so the field is mostly true black. A uniformly dim field would lift p25
 *    across the whole playfield; a sparse bright one lifts p99 of a few percent
 *    of pixels and leaves the floor alone. The three chains carrying a `mult`
 *    get their zeros from the mask instead, which is the same idea moved a stage
 *    earlier: a keyed mask kills its dead regions outright, so the pedestal
 *    underneath is only trimming what survives.
 *  - **Slow.** No `sync` above 0.05 and no scroll above 0.02/s. At 640x360 with
 *    a CRT mask on top, anything faster reads as noise and competes with the
 *    swarm for the eye.
 *  - **Wide tolerances.** `luma` tolerance never below 0.02. Hard edges on a
 *    moving field strobe against the aperture mask.
 *  - **Hue off the gameplay reads.** Blue and violet sit with the lattice;
 *    amber sits with the corridor. Nothing lands on `lime`, `cyan` or `yellow`
 *    at strength, because those are enemy families.
 */
interface Pattern {
  readonly name: string;
  /**
   * Per-pattern multiplier on `HYDRA.gain`. Sets how bright the lit part is.
   */
  readonly trim: number;
  /**
   * Per-pattern black point, overriding `HYDRA.pedestal`.
   *
   * This is the coverage knob and the more important of the two. Every chain
   * ends on a wide `luma` ramp, so the pattern arrives as a continuous field
   * from black to bright; where the pedestal cuts that ramp decides what
   * fraction of the frame survives at all. Raising it shrinks the lit area
   * without touching the peak, because the remainder is renormalised.
   *
   * Tuned per pattern against the rig, and the number it is tuned to is not
   * coverage but the sub-0.05 population: every pattern has to leave at least
   * ~6% of the background cells under 0.05, against 7.0% with the layer off.
   * That is the honest test of "sparse", because a pattern can cover a fifth of
   * the frame and still leave the dark quartile untouched, while a broad dim
   * wash covers the same fraction by lifting every dark pixel a little. The set
   * lands between 3.6% and 12.7% coverage on that constraint.
   */
  readonly pedestal: number;
}

/**
 * Set order, and the branch index the shader switches on. The `uPattern` cases
 * in `PATTERN_GLSL` below are these array positions; keep the two in step.
 */
const PATTERNS: readonly Pattern[] = [
  { name: 'nebula-drift', trim: 1.0, pedestal: 0.10 },
  { name: 'bar-drift', trim: 0.75, pedestal: 0.34 },
  { name: 'cell-walls', trim: 0.9, pedestal: 0.12 },
  { name: 'slow-kaleid', trim: 0.20, pedestal: 0.38 },
  { name: 'ember-drift', trim: 1.1, pedestal: 0.14 },
  { name: 'warp-echo', trim: 1.0, pedestal: 0.30 },
];

const VERT = /* glsl */ `
precision highp float;
in vec3 position;
in vec2 uv;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/**
 * The op set the chains are built from.
 *
 * Thirteen small functions: four sources, five coordinate transforms, four
 * colour transforms. Each one is written here from its own definition — a
 * travelling sine wave, a rotation matrix about the frame centre, a wrapped
 * translation, a Rec.709 luminance key, a per-channel gain, an F1 cellular
 * field, a mirrored angular fold. Nothing in this block is longer than the
 * definition it implements, because for a rotation or a luma key there is only
 * one thing to write.
 *
 * The thirteenth is `h_mask`, which the sketched chains did not have and the
 * `mult` patterns turned out to need: see its comment for why multiplying by a
 * raw signed field is not a mask.
 *
 * The `h_` prefix and the argument lists match the video-synth vocabulary the
 * patterns were sketched in, so a chain in a pattern's comment reads against
 * the code below without a translation table. That is an interface, not an
 * implementation: it says what `osc(frequency, sync, offset)` means, and the
 * body underneath is this file's own answer to it.
 *
 * The one thing not derived here is simplex noise, which is genuinely a piece
 * of published work rather than a formula you write out. It comes in from
 * `shaders/simplexNoise.ts` as the MIT-licensed Ashima Arts reference
 * implementation, attribution intact.
 */
const HYDRA_OPS = /* glsl */ `
// ITU-R BT.709 relative luminance. Same coefficients the post stack and the
// black-floor rig use, so a threshold quoted in one place means the same thing
// everywhere.
float _luma709(vec3 rgb) {
  return dot(rgb, vec3(0.2126, 0.7152, 0.0722));
}

// Counter-clockwise rotation of \`p\` by \`a\` radians. GLSL's matrix constructor
// takes columns, so the first pair is the image of the x axis.
vec2 _rot(vec2 p, float a) {
  float c = cos(a);
  float s = sin(a);
  return mat2(c, s, -s, c) * p;
}

// 32-bit integer avalanche: xor-shift, multiply, repeat. This is the standard
// hash-finalizer construction — any odd multiplier works, and these are the
// familiar golden-ratio and Murmur3 ones. It replaces the fract(sin(dot(...)))
// idiom because that idiom bands badly once the cell index grows and because
// its output depends on how a given driver rounds sin.
uint _mix32(uint x) {
  x ^= x >> 16;
  x *= 0x9e3779b9u;
  x ^= x >> 15;
  x *= 0x85ebca6bu;
  x ^= x >> 16;
  return x;
}

// Two independent values in [0, 1] per integer lattice cell. The bias keeps the
// cell index non-negative across the coordinate range the patterns reach.
vec2 _cellSeed(vec2 cell) {
  uvec2 c = uvec2(ivec2(floor(cell)) + 4096);
  uint h = _mix32(c.x ^ _mix32(c.y * 0x27d4eb2fu));
  return vec2(h & 0xffffu, (h >> 16) & 0xffffu) / 65535.0;
}

// ── sources ────────────────────────────────────────────────────────────────

// A slice of the 3D noise field, drifting along the third axis at \`offset\` per
// second. The value stays signed rather than being remapped to [0, 1]: every
// chain keys off this with a \`luma\` ramp whose foot sits near zero, so the
// negative half of the field is what makes the patterns sparse.
vec4 h_noise(vec2 _st, float scale, float offset) {
  return vec4(vec3(snoise(vec3(_st * scale, offset * uTime))), 1.0);
}

// The same field keyed into [0, 1], for use as a mask.
//
// Multiplying a pattern by the raw field does not work: the field is signed, so
// it inverts as often as it dims, and whether a given chain survives at all
// comes down to which lobe of a screen-wide blob happens to be under the frame.
// Keying it first gives a mask with the two properties a mask needs — true
// zeros over the dead regions, so the black floor is protected by the mask
// rather than by the pedestal, and a smooth shoulder into the live ones, so the
// patches have no edge to strobe. Threshold and tolerance mean what they mean
// in \`h_luma\`, measured against the signed field rather than a luminance.
vec4 h_mask(vec2 _st, float scale, float offset, float threshold, float tolerance) {
  float n = snoise(vec3(_st * scale, offset * uTime));
  float a = smoothstep(threshold - tolerance, threshold + tolerance, n);
  return vec4(vec3(a), 1.0);
}

// A travelling sine wave along x, read as brightness. Phase is
// \`(x + t * sync) * frequency\`, which is the wave equation f(x + ct) with the
// spatial frequency folded in, so \`sync\` is a drift rate in frame widths per
// second at any frequency. Red and blue are displaced from green by \`offset\`
// radians, which is what puts a colour fringe on the edge of each bar.
vec4 h_osc(vec2 _st, float frequency, float sync, float offset) {
  float phase = (_st.x + uTime * sync) * frequency;
  return vec4(
    sin(phase - offset) * 0.5 + 0.5,
    sin(phase) * 0.5 + 0.5,
    sin(phase + offset) * 0.5 + 0.5,
    1.0
  );
}

// F1 cellular noise. Cut the plane into unit cells at \`scale\`, give each cell
// one feature point orbiting its centre at \`speed\`, and shade every pixel by
// how far it sits from the nearest feature point. The walls fall out of that
// distance term: they are the locus where no point is close, so \`blending\`
// controls how dark the seams go.
//
// Brightness is a function of distance alone, so the field is continuous across
// cell boundaries — a per-cell brightness would put a hard step on every seam,
// which on a background under a CRT mask reads as blocking. The variation
// between cells comes from where each point sits inside its cell, plus one slow
// noise swell over the top so the lattice does not read as a quilt.
vec4 h_voronoi(vec2 _st, float scale, float speed, float blending) {
  vec2 sc = _st * scale;
  vec2 base = floor(sc);
  vec2 f = fract(sc);
  float nearest = 8.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 cell = vec2(float(i), float(j));
      // The point orbits rather than wanders, so a cell never hands its
      // territory over abruptly and the walls breathe instead of snapping.
      float a = 6.2831853 * _cellSeed(base + cell).x + uTime * speed;
      vec2 point = vec2(0.5) + 0.34 * vec2(cos(a), sin(a));
      nearest = min(nearest, length(cell + point - f));
    }
  }
  float shade = 1.0 - blending * nearest;
  shade *= 0.62 + 0.38 * (snoise(vec3(_st * 1.7, uTime * 0.02)) * 0.5 + 0.5);
  return vec4(vec3(shade), 1.0);
}

// ── coordinate transforms ──────────────────────────────────────────────────

// Turns the frame about its middle, the angle advancing at \`speed\` radians per
// second.
vec2 h_rotate(vec2 _st, float angle, float speed) {
  return vec2(0.5) + _rot(_st - vec2(0.5), angle + speed * uTime);
}

// Mirrored angular fold. The disc around the centre is cut into \`nSides\`
// wedges and each wedge folded in half about its own bisector, so the entire
// frame is described by one triangle of it and the seams land on the mirrors.
//
// The fold is a transform of the *offset* from the centre, so the centre goes
// back on afterwards. Leaving it off would hand the sources a coordinate half a
// unit away from where the rest of the chain works, which for an oscillator is
// a phase error and for a noise field is a different region of it.
vec2 h_kaleid(vec2 _st, float nSides) {
  vec2 p = _st - vec2(0.5);
  float wedge = 6.2831853 / nSides;
  float a = abs(mod(atan(p.y, p.x), wedge) - wedge * 0.5);
  return vec2(0.5) + length(p) * vec2(cos(a), sin(a));
}

// Vertical translation, wrapped. \`amount\` is a fixed offset and \`speed\` a rate
// in frame heights per second; the fract tiles the frame so the field slides
// forever instead of running off the end.
vec2 h_scrollY(vec2 _st, float amount, float speed) {
  return fract(vec2(_st.x, _st.y + amount + uTime * speed));
}

// Displacement. The modulating colour's first two channels are read as a
// vector and added to the coordinate at \`amount\` strength.
vec2 h_modulate(vec2 _st, vec4 _c0, float amount) {
  return _st + _c0.xy * amount;
}

// Rotation about the centre, but with the angle driven by the modulating
// colour: a fixed \`offset\` plus its red channel scaled by \`multiple\`.
vec2 h_modulateRotate(vec2 _st, vec4 _c0, float multiple, float offset) {
  return vec2(0.5) + _rot(_st - vec2(0.5), offset + _c0.x * multiple);
}

// ── colour transforms ──────────────────────────────────────────────────────

// Soft luminance key. Everything below \`threshold - tolerance\` is cut, above
// \`threshold + tolerance\` passes, and the smoothstep across the middle is what
// keeps the key edge from strobing against the aperture mask. Colour is
// premultiplied by the alpha it just computed, so the ramp reads as a fade
// rather than as a stencil.
vec4 h_luma(vec4 _c0, float threshold, float tolerance) {
  // A zero-width ramp would collapse smoothstep to a step.
  float t = max(tolerance, 1e-5);
  float a = smoothstep(threshold - t, threshold + t, _luma709(_c0.rgb));
  return vec4(_c0.rgb * a, a);
}

// Per-channel gain. Tints without disturbing the key, because the alpha is
// scaled alongside the colour it was premultiplied into.
vec4 h_color(vec4 _c0, float r, float g, float b, float a) {
  return _c0 * vec4(r, g, b, a);
}

// Saturation, as an interpolation between the achromatic luminance and the
// colour itself. 1.0 is a no-op and above 1.0 pushes past the original chroma.
vec4 h_saturate(vec4 _c0, float amount) {
  return vec4(mix(vec3(_luma709(_c0.rgb)), _c0.rgb, amount), _c0.a);
}

// Multiply with a blend depth. At 1.0 a plain product, at 0.0 the first colour
// untouched, so \`amount\` is how hard the mask bites rather than a second
// opacity.
vec4 h_mult(vec4 _c0, vec4 _c1, float amount) {
  return mix(_c0, _c0 * _c1, amount);
}
`;

/**
 * The six chains.
 *
 * Each function carries the chain it was sketched as, in the live-coding
 * notation the set was designed in. Reading one against the other needs the two
 * composition rules of that notation:
 *
 *  - A chain's *coordinate* transforms apply to `_st` in reverse order — the
 *    last one written runs first — while *colour* transforms apply in the order
 *    written. So `osc().kaleid().modulate()` modulates the coordinate, then
 *    kaleidoscopes it, then samples the oscillator at the result.
 *  - A source handed to `modulate` or `mult` reads the coordinate as it stands
 *    at that point in the chain, which is to say after every transform written
 *    *below* it and none of the ones above. That is why each `mult` mask here
 *    samples the raw `st`: it sits last in its chain, so nothing has displaced
 *    the coordinate yet, and the mask stays pinned to the screen while the
 *    pattern under it warps.
 */
const PATTERN_GLSL = /* glsl */ `
// noise(1.5, 0.014).modulate(noise(0.85, 0.005), 0.42).luma(0.20, 0.44).color(0.34, 0.5, 1.0)
//
// A noise field displaced by a slower one. The plainest thing in the set, and
// the one that reads as depth rather than as decoration.
//
// Retuned: feature scale down from 2.2 and the ramp foot down from 0.42, both
// because the reference simplex kernel is slightly tighter than the field this
// was first sketched against. Bigger blobs lit harder, rather than the same
// blobs spread thinner — the pedestal owns coverage and this owns the shape.
vec4 p_nebulaDrift(vec2 st) {
  vec4 m = h_noise(st, 0.85, 0.005);
  st = h_modulate(st, m, 0.42);
  vec4 c = h_noise(st, 1.5, 0.014);
  c = h_luma(c, 0.20, 0.44);
  return h_color(c, 0.34, 0.5, 1.0, 1.0);
}

// osc(14, 0.011, 0.35).rotate(0.5, 0.008).modulate(noise(1.1, 0.004), 0.3)
//   .luma(0.44, 0.4).mult(mask(2.4, 0.003)).color(0.26, 0.46, 1.0)
//
// Soft bars raked across the field and pulled apart by a slow noise, so the
// structure drifts between banded and dissolved instead of holding still. The
// mask breaks the bars into drifting patches; without it the pattern is a
// full-field corduroy and reads as a rendering fault rather than as weather.
//
// Retuned: the mask went from a raw noise field at scale 1.0 to a keyed one at
// 2.4. At scale 1.0 a single blob spans the whole frame, so the mask was not
// breaking the bars into patches at all — it was deciding, once per hold and
// by luck, whether the pattern appeared.
vec4 p_barDrift(vec2 st) {
  vec4 mask = h_mask(st, 2.4, 0.003, 0.25, 0.45);
  vec4 m = h_noise(st, 1.1, 0.004);
  vec2 s = h_rotate(h_modulate(st, m, 0.3), 0.5, 0.008);
  vec4 c = h_osc(s, 14.0, 0.011, 0.35);
  c = h_luma(c, 0.44, 0.4);
  c = h_mult(c, mask, 1.0);
  return h_color(c, 0.26, 0.46, 1.0, 1.0);
}

// voronoi(8, 0.055, 0.9).modulate(noise(0.8, 0.004), 0.12).luma(0.66, 0.30).color(0.42, 0.36, 1.0)
//
// Cellular walls. blending high keeps the cells soft, so the ramp catches the
// bright cell interiors and lets the seams fall away under the pedestal.
//
// Retuned: ramp up from 0.46 to 0.66. The cellular field here is continuous by
// construction and so has no true zeros of its own, which left the whole frame
// faintly lit; the ramp now makes the zeros the field does not.
vec4 p_cellWalls(vec2 st) {
  vec4 m = h_noise(st, 0.8, 0.004);
  st = h_modulate(st, m, 0.12);
  vec4 c = h_voronoi(st, 8.0, 0.055, 0.9);
  c = h_luma(c, 0.66, 0.30);
  return h_color(c, 0.42, 0.36, 1.0, 1.0);
}

// osc(9.5, 0.009, 0.5).kaleid(6).modulateRotate(osc(2.2, 0.004), 0.18)
//   .luma(0.52, 0.38).mult(mask(2.6, 0.003)).saturate(1.2)
//
// The signature move of the idiom — a kaleidoscope displaced by a slower
// oscillator. modulateRotate depth is capped at 0.18; past ~0.4 the whole field
// starts wheeling and the eye follows it instead of the corridor.
//
// Retuned: oscillator frequency up from 7. A correct fold returns a coordinate
// centred on 0.5 rather than on the origin, which shifts the phase the ring
// lands at by half a frame width; at frequency 7 the one bright band fell in
// the corners. 9.5 puts it back at about a third of the radius. Trim came down
// with it, because a ring in frame is a great deal more light than a ring in
// the corners.
vec4 p_slowKaleid(vec2 st) {
  vec4 mask = h_mask(st, 2.6, 0.003, 0.25, 0.45);
  vec4 m = h_osc(st, 2.2, 0.004, 0.0);
  vec2 s = h_kaleid(h_modulateRotate(st, m, 0.18, 0.0), 6.0);
  vec4 c = h_osc(s, 9.5, 0.009, 0.5);
  c = h_luma(c, 0.52, 0.38);
  c = h_mult(c, mask, 1.0);
  return h_saturate(c, 1.2);
}

// noise(6.5, 0.02).modulate(noise(1.4, 0.006), 0.2).scrollY(0, -0.012)
//   .luma(0.56, 0.32).color(1.0, 0.52, 0.2)
//
// Warm embers rising. The one pattern carrying a hue near the corridor's ochre,
// kept to a low ceiling by the tightest ramp in the set so there is never
// enough of it on screen to compete with the track.
vec4 p_emberDrift(vec2 st) {
  st = h_scrollY(st, 0.0, -0.012);
  vec4 m = h_noise(st, 1.4, 0.006);
  st = h_modulate(st, m, 0.2);
  vec4 c = h_noise(st, 6.5, 0.02);
  c = h_luma(c, 0.56, 0.32);
  return h_color(c, 1.0, 0.52, 0.2, 1.0);
}

// osc(10.5, 0.007, 1.1).kaleid(3).modulate(src(o0), 0.07)
//   .luma(0.48, 0.4).mult(mask(2.4, 0.003)).color(0.55, 0.4, 1.0)
//
// Retuned the same way its six-sided sibling was, and harder: at frequency 5 a
// fold centred on 0.5 put the whole frame inside the dark half of one sine
// period, so the pattern rendered black at any gain. 10.5 lands a single ring
// at about a quarter of the radius, with a dark centre and a dark rim.
//
// The feedback chain, minus the feedback buffer. src(o0) displaced the
// coordinate by the *previous frame's* output of this same chain, which is a
// fixed point: with the base oscillator running at sync 0.007 and the mask at
// offset 0.003, last frame's field and this frame's differ by about a thousandth
// of a cycle, so the previous frame is the undisplaced present frame to well
// under a byte. Evaluating the chain twice — once undisplaced to get the echo,
// once displaced by it — lands on that fixed point directly, for the cost of one
// extra noise and six extra sines and no render target at all.
//
// A real ping-pong target would have bought the one-frame lag and cost the
// determinism: a buffer carrying state across pattern switches, scenario resets
// and governor rungs is a buffer the capture harness cannot reproduce, and
// reproducibility is the whole reason this layer runs off a frame counter.
vec4 p_warpEcho(vec2 st, vec2 disp) {
  vec4 mask = h_mask(st, 2.4, 0.003, 0.25, 0.45);
  vec2 s = h_kaleid(st + disp, 3.0);
  vec4 c = h_osc(s, 10.5, 0.007, 1.1);
  c = h_luma(c, 0.48, 0.4);
  c = h_mult(c, mask, 1.0);
  return h_color(c, 0.55, 0.4, 1.0, 1.0);
}

vec3 patternColor(vec2 st) {
  if (uPattern == 0) return p_nebulaDrift(st).rgb;
  if (uPattern == 1) return p_barDrift(st).rgb;
  if (uPattern == 2) return p_cellWalls(st).rgb;
  if (uPattern == 3) return p_slowKaleid(st).rgb;
  if (uPattern == 4) return p_emberDrift(st).rgb;
  if (uPattern == 5) return p_warpEcho(st, p_warpEcho(st, vec2(0.0)).xy * 0.07).rgb;
  return vec3(0.0);
}
`;

/**
 * The whole safety argument, in six lines of GLSL.
 *
 * The pattern value is clamped to `[0, 1]` first, which is what the old 8-bit
 * output buffer used to do and what keeps `uGain` a genuine ceiling rather than
 * a nominal one. `uPedestal` is subtracted next and the result clamped, so the
 * dark part of the pattern writes literal zero into an additive blend and the
 * frame below is untouched. The remainder is renormalised so the pedestal costs
 * contrast rather than peak brightness, then scaled by a gain that cannot reach
 * the bloom threshold.
 *
 * The sRGB decode outlived the texture it was written for. The old path put the
 * chains into an 8-bit sRGB surface and every pedestal was measured against the
 * linear value that came back out, so the decode is what keeps those numbers
 * meaning what they meant. Dropping it would silently move six black points.
 */
const FRAG = /* glsl */ `
precision highp float;
// The cellular hash needs 32 bits to wrap in. Fragment integers default to
// mediump, which ESSL only guarantees to 16.
precision highp int;

uniform float uGain;
uniform float uPedestal;
uniform float uTime;
uniform int uPattern;

in vec2 vUv;
out vec4 fragColor;

${GLSL_SIMPLEX_NOISE_3D}
${HYDRA_OPS}
${PATTERN_GLSL}

vec3 srgbToLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}

void main() {
  vec3 c = srgbToLinear(clamp(patternColor(vUv), 0.0, 1.0));
  c = max(c - uPedestal, 0.0) / max(1e-4, 1.0 - uPedestal);
  fragColor = vec4(c * uGain, 1.0);
}
`;

export interface HydraBackgroundOptions {
  /** Off keeps the quad hidden and the pass unsubmitted. */
  enabled?: boolean;
  /** Overrides the peak linear gain. Clamped well under the bloom knee foot. */
  gain?: number;
  /** Start on a named pattern instead of the first. */
  pattern?: string;
}

/** How the layer reports itself to the settings panel and the perf HUD. */
export interface HydraStatus {
  /** The player's switch. */
  enabled: boolean;
  /** True only when enabled, allowed by the governor and drawing. */
  active: boolean;
  /**
   * Kept from the hydra build, where it stayed false until a 1.8 MB dynamic
   * import resolved. The patterns are a shader now, so there is nothing to wait
   * for and nothing that can fail to arrive.
   */
  loaded: boolean;
  /** The governor pulled rung 1. */
  suppressed: boolean;
  reducedMotion: boolean;
  pattern: string;
  patterns: readonly string[];
}

export class HydraBackground {
  /** Add this to `renderer.scene`. It carries its own render order. */
  readonly object: THREE.Mesh;

  private readonly material: THREE.RawShaderMaterial;
  private geometry: THREE.PlaneGeometry;
  /** Last region generation the quad was cut for. */
  private regionGeneration = -1;
  private readonly uniforms: {
    uGain: THREE.IUniform<number>;
    uPedestal: THREE.IUniform<number>;
    uTime: THREE.IUniform<number>;
    uPattern: THREE.IUniform<number>;
  };

  private wanted: boolean;
  private readonly baseGain: number;
  private patternIndex = 0;
  /**
   * Frames drawn since the layer went live. Everything the field does is
   * scheduled off this and nothing else.
   *
   * The argument to `update` is deliberately ignored. The first version
   * differenced it to get a `dt`, which made the layer hostage to whichever
   * clock the caller happened to pass: `main.ts` wires it to `world.time`, and
   * sim time stops dead while the game is paused and runs at treble rate on the
   * 3x speed control — so the field froze between rounds and sprinted during a
   * fight. A private frame counter is immune to both, and it keeps the capture
   * harness deterministic for the same reason `PostStack.time` is derived from
   * `Renderer`'s counter rather than from `performance.now`.
   */
  private frames = 0;
  /** Frame index of the last pattern activation. */
  private patternAtFrame = 0;
  private reducedMotion = false;
  private motionQuery: MediaQueryList | null = null;
  private lastGeneration = perf.generation;
  /** Rig overrides, set by `tune`. Null means the pattern's own value wins. */
  private gainOverride: number | null = null;
  private pedestalOverride: number | null = null;

  private readonly onMotionChange = (): void => {
    this.reducedMotion = this.motionQuery?.matches === true;
  };

  constructor(options: HydraBackgroundOptions = {}) {
    this.wanted = options.enabled ?? true;
    // Hard ceiling on the override. Even a caller that asks for something silly
    // stays two thirds of the way below the bloom knee foot at 0.85, so no
    // configuration of this layer can reach the bright pass.
    this.baseGain = Math.min(options.gain ?? HYDRA.gain, 0.35);

    const start = PATTERNS.findIndex((p) => p.name === options.pattern);
    if (start >= 0) this.patternIndex = start;

    this.uniforms = {
      uGain: { value: 0 },
      uPedestal: { value: HYDRA.pedestal },
      uTime: { value: 0 },
      uPattern: { value: this.patternIndex },
    };

    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms as unknown as Record<string, THREE.IUniform>,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      // Explicit One/One rather than AdditiveBlending: the default premultiplies
      // by alpha, and this material has no meaningful alpha to premultiply by.
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
    });

    this.geometry = new THREE.PlaneGeometry(VIEW.width, VIEW.height);
    this.object = new THREE.Mesh(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.fitRegion();
    // Below `WarpGrid`'s -100 and the corridor's -60, which is the entire point:
    // the lattice, the track and every sprite draw over this, never under it.
    this.object.renderOrder = -200;
    this.object.visible = false;

    this.watchReducedMotion();
    // Debug surface, same shape as `core/perf.ts`'s `__pixeltdPerf`. The visual
    // rig has to photograph one named pattern at a time, and rebuilding the
    // bundle six times to do it is not a workflow.
    if (typeof window !== 'undefined') {
      (window as unknown as { __pixeltdHydra?: HydraBackground }).__pixeltdHydra = this;
    }
  }

  /** Names in set order, for the settings panel's pattern selector. */
  get patterns(): readonly string[] {
    return PATTERNS.map((p) => p.name);
  }

  get pattern(): string {
    return PATTERNS[this.patternIndex].name;
  }

  /** The player's switch. */
  setEnabled(on: boolean): void {
    if (this.wanted === on) return;
    this.wanted = on;
    if (!on) this.object.visible = false;
  }

  /** Jumps to a named pattern and restarts its hold. Unknown names are ignored. */
  setPattern(name: string): void {
    const i = PATTERNS.findIndex((p) => p.name === name);
    if (i < 0 || i === this.patternIndex) return;
    this.patternIndex = i;
    this.patternAtFrame = this.frames;
    this.uniforms.uPattern.value = i;
  }

  status(): HydraStatus {
    return {
      enabled: this.wanted,
      active: this.object.visible,
      loaded: true,
      suppressed: !perf.caps.hydra,
      reducedMotion: this.reducedMotion,
      pattern: this.pattern,
      patterns: this.patterns,
    };
  }

  /**
   * One frame.
   *
   * The parameter is accepted and ignored: see `frames`. Call it once per
   * rendered frame from anywhere — `Renderer.background` does this for you, and
   * that remains the recommended wiring because it guarantees the call lands
   * before the scene draw, so the uniforms the quad reads are this frame's.
   */
  /**
   * Re-cuts the quad to the rendered region.
   *
   * The region is always centred on the band, so the mesh never moves and only
   * its extent does. The UVs stay `0..1` across whatever that extent is, which
   * means a region taller than the band shows a *stretched* field rather than
   * more of one — and that is deliberate.
   *
   * The obvious alternative is to scale the UVs by the same ratio, so the band
   * keeps its own `0..1` and the extra glass continues the pattern past the
   * edge at its authored size. That was the first version and it turned the
   * whole tube black on a portrait phone. Every chain here is written over the
   * unit square, and outside it at least one op returns NaN; the NaN lands in
   * the scene buffer, `Persistence` takes `max(current, trail)` and keeps it
   * forever, and the composite multiplies the frame by it. Measured: a 405x870
   * window rendered 30 draw calls and 12330 triangles into a canvas whose mean
   * luminance was exactly 0, and setting `persistence` to 0 brought the whole
   * picture back.
   *
   * Two further reasons not to go back to it once the NaN is scrubbed. The
   * black-floor numbers this file is built around — coverage, `pedestal`, the
   * p25 the layer is allowed to move — were all measured over the unit square,
   * so a field running past it is a field nobody has measured. And the layer is
   * atmosphere: gain 0.20, arithmetically unable to reach the bloom threshold,
   * and the governor's very first rung switches it off. A stretched abstract
   * field is a cheap price; a black tube is not a price at all.
   */
  private fitRegion(): void {
    const view = region.current;
    this.regionGeneration = region.generation;

    const previous = this.geometry;
    this.geometry = new THREE.PlaneGeometry(view.width, view.height);
    this.object.geometry = this.geometry;
    // The region is centred on the band, so this point never actually moves.
    this.object.position.set(VIEW.width * 0.5, VIEW.height * 0.5, 0);
    previous.dispose();
  }

  update(_timeSeconds?: number): void {
    // A rung change is the only thing that can switch the layer off underneath
    // the player, and it is worth noticing exactly once rather than per frame.
    if (this.lastGeneration !== perf.generation) {
      this.lastGeneration = perf.generation;
    }
    if (this.regionGeneration !== region.generation) this.fitRegion();

    const on = this.wanted && perf.caps.hydra;
    if (!on) {
      this.object.visible = false;
      return;
    }

    // Reduced motion freezes the field outright. The frame counter stops, so
    // `uTime` stops, so the shader returns the same picture every frame. It
    // costs the same as a moving one, which is the honest trade for a layer with
    // no texture upload left to skip.
    if (!this.reducedMotion) {
      this.frames++;

      // Advance the set. The fade is symmetric around the switch, so the
      // pattern changes at gain zero and no frame ever shows a hard cut.
      const heldFrames = this.frames - this.patternAtFrame;
      if (heldFrames >= (HYDRA.holdSeconds + HYDRA.fadeSeconds) * 60) {
        this.patternIndex = (this.patternIndex + 1) % PATTERNS.length;
        this.patternAtFrame = this.frames;
        this.uniforms.uPattern.value = this.patternIndex;
      }
    }

    // hydra accumulated `dt * 0.001 * speed` per tick at a fixed 1/60 dt. Same
    // number, computed rather than accumulated, so it cannot drift.
    this.uniforms.uTime.value = (this.frames / 60) * HYDRA.timeScale;

    const active = PATTERNS[this.patternIndex];
    this.object.visible = true;
    this.uniforms.uPedestal.value = this.pedestalOverride ?? active.pedestal;
    this.uniforms.uGain.value =
      (this.gainOverride ?? this.baseGain) *
      active.trim *
      (this.reducedMotion ? 1 : this.envelope((this.frames - this.patternAtFrame) / 60));
  }

  /**
   * Overrides the two tuning numbers live. `null` hands a knob back to the
   * pattern's own value.
   *
   * The visual-critic loop needs to sweep these against the black-floor rig
   * without a rebuild per sample, exactly as `PostStack.set` exists for the CRT
   * parameters. Nothing in the game calls this.
   */
  tune(gain: number | null, pedestal: number | null): void {
    this.gainOverride = gain === null ? null : Math.min(Math.max(gain, 0), 0.35);
    this.pedestalOverride = pedestal === null ? null : Math.min(Math.max(pedestal, 0), 0.95);
  }

  dispose(): void {
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    this.motionQuery = null;
    this.material.dispose();
    this.geometry.dispose();
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  /**
   * Fade in, hold, fade out. Zero at both ends of the pattern's life.
   *
   * Cosine rather than linear: a linear ramp on an additive layer has a visible
   * kink at both ends, which on a background is the one thing that draws the
   * eye to it.
   */
  private envelope(held: number): number {
    const f = HYDRA.fadeSeconds;
    if (held < f) return 0.5 - 0.5 * Math.cos((held / f) * Math.PI);
    const out = HYDRA.holdSeconds + f - held;
    if (out < f) return 0.5 - 0.5 * Math.cos((Math.max(0, out) / f) * Math.PI);
    return 1;
  }

  private watchReducedMotion(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.motionQuery.matches;
    this.motionQuery.addEventListener('change', this.onMotionChange);
  }
}
